/**
 * Promo Popup JavaScript
 * Professional promotional popup with test mode, timer, and success modal support
 */

class PromoPopup {
  constructor(element) {
    this.popup = element;
    if (!this.popup) return;

    this.closeButtons = this.popup.querySelectorAll('.promo-popup-section__close-btn');
    this.form = this.popup.querySelector('#promo-popup-form');
    this.isTestMode = this.popup.dataset.testMode === 'true';
    this.delaySeconds = parseInt(this.popup.dataset.delaySeconds) || 5;
    this.delayDays = parseInt(this.popup.dataset.delayDays) || 30;
    this.displayTimer = this.popup.dataset.displayTimer === 'true';
    this.timerDuration = parseFloat(this.popup.dataset.timerDuration) || 3;
    this.popupId = this.popup.id || 'promo-popup';
    this.storageKey = `promo_popup_dismissed_${this.popupId}`;
    this.timerInterval = null;
    this.timerEndTime = null;
    
    this.init();
  }

  init() {
    // Don't show in theme editor
    if (document.body.classList.contains('shopify-section-editor-active')) {
      return;
    }

    // Setup close handlers
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Test mode - show immediately
    if (this.isTestMode) {
      this.open();
      if (this.displayTimer) {
        this.startTimer();
      }
      return;
    }

    // Check if popup should be shown
    if (!this.shouldShowPopup()) {
      return;
    }

    // Show popup after delay
    setTimeout(() => {
      if (!this.popup.classList.contains('promo-popup-section--hidden')) {
        this.open();
        if (this.displayTimer) {
          this.startTimer();
        }
      }
    }, this.delaySeconds * 1000);

    // Check if form was already submitted successfully
    this.checkFormSuccess();
  }

  shouldShowPopup() {
    // In test mode, always show
    if (this.isTestMode) {
      return true;
    }

    // Check if popup was dismissed
    const dismissedData = localStorage.getItem(this.storageKey);
    if (!dismissedData) {
      return true;
    }

    try {
      const data = JSON.parse(dismissedData);
      const dismissedDate = new Date(data.dismissedAt);
      const now = new Date();
      const daysSinceDismissed = Math.floor((now - dismissedDate) / (1000 * 60 * 60 * 24));

      // Show again if enough days have passed
      return daysSinceDismissed >= this.delayDays;
    } catch (e) {
      // If data is corrupted, show popup
      return true;
    }
  }

  startTimer() {
    const minutesEl = this.popup.querySelector('.promo-popup-section__timer__minutes');
    const secondsEl = this.popup.querySelector('.promo-popup-section__timer__seconds');
    
    if (!minutesEl || !secondsEl) return;

    // Calculate end time
    const now = new Date().getTime();
    this.timerEndTime = now + (this.timerDuration * 60 * 1000);

    // Update timer immediately
    this.updateTimer(minutesEl, secondsEl);

    // Update timer every second
    this.timerInterval = setInterval(() => {
      if (this.updateTimer(minutesEl, secondsEl)) {
        // Timer expired
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    }, 1000);
  }

  updateTimer(minutesEl, secondsEl) {
    const now = new Date().getTime();
    const remaining = Math.max(0, this.timerEndTime - now);
    
    if (remaining <= 0) {
      minutesEl.textContent = '00';
      secondsEl.textContent = '00';
      return true; // Timer expired
    }

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    minutesEl.textContent = String(minutes).padStart(2, '0');
    secondsEl.textContent = String(seconds).padStart(2, '0');
    
    return false; // Timer still running
  }

  isOpen() {
    return this.popup?.classList.contains('promo-popup-section--active');
  }

  open() {
    if (!this.popup) return;
    
    this.popup.classList.add('promo-popup-section--active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.popup) return;
    
    this.popup.classList.remove('promo-popup-section--active');
    document.body.style.overflow = '';

    // Stop timer if running
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    // Save dismissal to localStorage (only if not in test mode)
    if (!this.isTestMode) {
      const dismissedData = {
        dismissedAt: new Date().toISOString()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(dismissedData));
    }
  }

  checkFormSuccess() {
    const successMessage = this.form?.querySelector('.newsletter-form__message');
    if (successMessage && successMessage.classList.contains('form__message')) {
      // Form has errors, don't close
      return;
    }
  }
}

// Success Popup Handler
class SuccessPopup {
  constructor() {
    this.overlay = document.querySelector('.success-popup-section__overlay');
    this.popup = document.querySelector('.success-popup-section');
    
    if (!this.popup) return;
    
    this.closeButtons = this.popup.querySelectorAll('.success-popup-section__close-btn, .success-popup-section .promo-popup-section__dismiss-btn');
    this.copyBtn = this.popup.querySelector('.promo-popup-section__copy-btn');
    this.copySuccessMsg = this.popup.querySelector('.promo-popup-section__success-msg');
    
    this.init();
  }

  init() {
    // Don't show in theme editor
    if (document.body.classList.contains('shopify-section-editor-active')) {
      return;
    }

    // Close handlers
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Copy button handler
    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', () => {
        const discountCodeInput = this.popup.querySelector('[id^="DiscountCode"]');
        if (!discountCodeInput) return;
        
        const discountCode = discountCodeInput.value || '';
        if (discountCode) {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(discountCode).then(() => {
              if (this.copySuccessMsg) {
                this.copySuccessMsg.style.display = 'block';
                setTimeout(() => {
                  this.copySuccessMsg.style.display = 'none';
                }, 3000);
              }
            }).catch(() => {
              this.fallbackCopy(discountCodeInput);
            });
          } else {
            this.fallbackCopy(discountCodeInput);
          }
        }
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  fallbackCopy(input) {
    if (input) {
      input.select();
      input.setSelectionRange(0, 99999);
      document.execCommand('copy');
      if (this.copySuccessMsg) {
        this.copySuccessMsg.style.display = 'block';
        setTimeout(() => {
          this.copySuccessMsg.style.display = 'none';
        }, 3000);
      }
    }
  }

  isOpen() {
    return this.popup?.classList.contains('success-popup-section--active');
  }

  open() {
    if (!this.popup) return;
    
    // Close main popup first
    const mainPopup = document.querySelector('[data-promo-popup]');
    if (mainPopup) {
      mainPopup.classList.remove('promo-popup-section--active');
    }
    
    this.popup.classList.add('success-popup-section--active');
    if (this.overlay) {
      this.overlay.classList.add('success-popup-section__overlay--active');
    }
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.popup) return;
    
    this.popup.classList.remove('success-popup-section--active');
    if (this.overlay) {
      this.overlay.classList.remove('success-popup-section__overlay--active');
    }
    document.body.style.overflow = '';
  }
}

// Initialize popups on DOM ready
function initPromoPopups() {
  const popups = document.querySelectorAll('[data-promo-popup]');
  popups.forEach(popup => {
    new PromoPopup(popup);
  });

  // Initialize success popup
  new SuccessPopup();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPromoPopups);
} else {
  initPromoPopups();
}
